import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, ExternalLink, Instagram } from 'lucide-react';
import SocialLinks, { SOCIAL_FIELDS, countSocialLinks, normalizeSocialUrl } from './SocialLinks';
import { motion } from 'framer-motion';
import type { PlaceResult } from '../hooks/useGooglePlaces';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { AdminItem, AdminAddBar } from '../admin/AdminControls';
import type { FieldDef } from '../admin/AdminFieldModal';
import NewsTicker from './NewsTicker';

const CAFE_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Name', required: true },
  { key: 'location', label: 'Location', placeholder: 'Frisco' },
  { key: 'city', label: 'City', placeholder: 'Frisco' },
  { key: 'address', label: 'Address', type: 'textarea' },
  { key: 'image', label: 'Image URL', type: 'image', placeholder: 'https://…' },
  { key: 'mapsUrl', label: 'Google Maps URL', type: 'url' },
  ...SOCIAL_FIELDS,
];

// Curated cafe entries live here (same pattern as DESSERTS_DATA in Desserts.tsx).
// `detailDescription` is detail-page-only copy (never shown on the card).
// `hideCardActions` hides the card's social row / Instagram button while the
// detail page still reads the links from the entry (same pattern as Kids.tsx).
export const CAFES_DATA: (PlaceResult & {
  instagramUrl?: string;
  imageFit?: 'cover' | 'contain';
  detailDescription?: string;
  hideCardActions?: boolean;
})[] = [
  {
    id: 'cafe-1',
    name: 'Froth Coffee Bar',
    location: 'Denton',
    city: 'Denton',
    address: '1611 Eagle Dr Ste 113, Denton, TX 76201',
    rating: 0,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-10-24/7160cd353ee7.jpeg',
    placeId: 'cafe-1',
    mapsUrl: 'https://www.google.com/maps/search/Froth+Coffee+Bar+1611+Eagle+Dr+Ste+113+Denton+TX+76201',
    cuisines: ['Cafe'],
    instagramUrl: 'https://www.instagram.com/frothcoffee_bar/?hl=en',
    detailDescription:
      "Welcome to Froth, Denton’s go-to coffee spot! We are proudly locally owned and just minutes from UNT campus and Denton Square. Whether you are grabbing your morning coffee before work, needing a spot for a study session, or catching up with friends, we are here to fuel your day and be a home away from home. Great coffee, good vibes, and a place to stay awhile. ☕️✨",
    hideCardActions: true,
  },
];

const HERO_LOCATIONS = ['All Locations', 'Frisco', 'Plano', 'Irving', 'Lewisville', 'McKinney', 'Dallas', 'Carrollton', 'The Colony', 'Allen', 'Prosper', 'Aubrey', 'Little Elm', 'Melissa', 'FlowerMound', 'Richardson', 'Addison', 'Denton', 'Online', 'Other'];

// Every cafe listing's stable slug (used for future detail pages, same pattern as dessertSlug).
export function cafeSlug(c: { id?: string; name?: string }) {
  return slugify(c.id || c.name || '');
}

// Admin-added cards often only fill in the Address field and leave the
// Google Maps URL blank. Auto-derive a maps search link from the address
// so the "View Location" button always appears once an address is set
// (skip handle-style addresses like "@some_handle").
function resolveMapsUrl(cafe: PlaceResult) {
  if (cafe.mapsUrl && cafe.mapsUrl.trim()) return cafe.mapsUrl;
  const address = (cafe as any).address as string | undefined;
  if (address && address.trim() && !address.trim().startsWith('@')) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`;
  }
  return '';
}

export function CafeCard({ cafe, index }: { cafe: PlaceResult & { instagramUrl?: string; hideCardActions?: boolean }; index: number }) {
  // Instagram can arrive via the legacy static `instagramUrl` field or the
  // admin-added SOCIAL_FIELDS `instagram` key — resolve both.
  const resolvedInstagramUrl =
    normalizeSocialUrl(cafe.instagramUrl, 'instagram') ||
    normalizeSocialUrl((cafe as any).instagram, 'instagram') ||
    normalizeSocialUrl((cafe as any).instagram_url, 'instagram');
  const hasInstagram = !!resolvedInstagramUrl;
  const resolvedMapsUrl = resolveMapsUrl(cafe);
  const hasAnySocial = countSocialLinks(cafe) > 0;
  // Detail-page-only links (per entry): hide the card's social row and
  // Instagram button; the detail page still reads them from the entry.
  const hideCardActions = !!(cafe as any).hideCardActions;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <Link to={'/cafe/' + cafeSlug(cafe)} className="flex flex-col flex-1">
        <div className="w-full h-[110px] relative overflow-hidden bg-gray-100">
          <img
            src={cafe.image}
            alt={cafe.name}
            className={(cafe as any).imageFit === 'contain' ? 'w-full h-full object-contain' : 'w-full h-full object-cover'}
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
        <div className="p-2.5 pb-1 flex-1">
          <h3 className="text-[13px] font-bold text-gray-900 leading-tight mb-0.5 line-clamp-2">{cafe.name}</h3>
          {!!(cafe.address && cafe.address.trim()) && (
            <div className="flex items-start gap-1">
              <MapPin className="w-2.5 h-2.5 text-amber-500 shrink-0 mt-0.5" />
              <span className="text-[10px] text-gray-500 font-medium leading-snug line-clamp-2">{cafe.address}</span>
            </div>
          )}
          {hasAnySocial && !hideCardActions && (
            <div className="pt-1">
              <SocialLinks item={cafe} px={20} />
            </div>
          )}
        </div>
      </Link>
      <div className="px-2.5 pb-2.5 pt-1 flex items-center gap-1.5">
        {hasInstagram && !hideCardActions && (
          <a
            href={resolvedInstagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex-1 flex items-center justify-center gap-1 bg-gradient-to-br from-pink-500 to-orange-400 hover:opacity-90 text-white text-[10px] font-semibold py-1.5 rounded-md transition-colors duration-200"
          >
            <Instagram className="w-2.5 h-2.5" />
            Instagram
          </a>
        )}
        {!!resolvedMapsUrl && (
          <a
            href={resolvedMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1 bg-[#51ba4f] hover:bg-[#43a041] text-white text-[10px] font-semibold py-1.5 rounded-md transition-colors duration-200"
          >
            <ExternalLink className="w-2.5 h-2.5" />
            View Location
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function Cafes() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHeroLocation, setSelectedHeroLocation] = useState('All Locations');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const mc = useManagedCollection<any>('cafes', CAFES_DATA, (c, i) => slugify(c.id || c.name || String(i)));

  const effectiveLocation = selectedLocation ?? (selectedHeroLocation !== 'All Locations' ? selectedHeroLocation : null);

  const places = effectiveLocation
    ? mc.items.filter((c: any) => c.location === effectiveLocation)
    : mc.items;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const filteredCafes = places.filter((r: any) => {
    const matchesQuery = searchQuery.trim() === '' || r.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = !effectiveLocation || r.location === effectiveLocation;
    return matchesQuery && matchesLocation;
  });

  const hasActiveFilters = effectiveLocation;

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        .filter-scroll::-webkit-scrollbar { display: none; }
        .filter-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Hero */}
      <section className="relative w-full" style={{ minHeight: "160px" }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=1600&h=500&fit=crop"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/45" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: "absolute", left: 0, bottom: "-1px", width: "100%", height: "18px", display: "block" }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex items-center justify-center" style={{ zIndex: 10, minHeight: "160px" }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[740px]">
            <div className="rounded-md" style={{ boxShadow: '0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <div className="flex flex-col sm:flex-row bg-white rounded-md shadow-md h-auto sm:h-[40px]">
                <div className="flex-1 px-3 py-2 sm:py-0 flex items-center border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">which cafe?</span>
                  <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="e.g. Chai, Coffee, Filter Kaapi" className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent" />
                </div>
                <div ref={dropdownRef} className="relative border-b sm:border-b-0 sm:border-r border-gray-200 min-h-[40px] sm:w-[170px] md:w-[190px] shrink-0" style={{ zIndex: 99999 }}>
                  <button onClick={() => setShowDropdown(!showDropdown)} className="w-full h-full px-3 py-2 sm:py-0 flex items-center gap-1.5 text-left min-h-[40px]">
                    <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{selectedHeroLocation}</span>
                    <svg className={'w-3 h-3 text-gray-400 shrink-0 transition-transform ' + (showDropdown ? 'rotate-180' : '')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showDropdown && (
                    <div
                      className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg py-1 overflow-y-auto"
                      style={{ zIndex: 99999, marginTop: '4px', width: '200px', maxHeight: '220px', boxShadow: '0 10px 24px rgba(15,23,42,0.16)', fontFamily: "'Poppins', sans-serif" }}
                    >
                      {HERO_LOCATIONS.map((loc) => (
                        <button
                          key={loc}
                          onClick={() => { setSelectedHeroLocation(loc); setShowDropdown(false); }}
                          className="w-full text-left flex items-center transition-colors duration-150"
                          style={{
                            padding: '8px 12px',
                            fontSize: '13px',
                            fontWeight: selectedHeroLocation === loc ? 700 : 500,
                            color: selectedHeroLocation === loc ? '#f59e0b' : '#111827',
                            background: selectedHeroLocation === loc ? '#fffbeb' : 'transparent',
                            height: '36px',
                          }}
                          onMouseEnter={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = '#fffbeb'; }}
                          onMouseLeave={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                        >
                          {loc}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => setShowDropdown(false)} className="bg-[#f97316] hover:bg-[#ea580c] text-white text-[13px] font-bold px-5 py-2.5 sm:py-0 transition-colors duration-200 shrink-0 min-h-[40px] sm:rounded-r-md w-full sm:w-auto">
                  <span className="flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5 sm:hidden" />
                    Search
                  </span>
                </button>
              </div>
            </div>

          </motion.div>
        </div>
      </section>
      <NewsTicker />

      {/* ── Cafe Listings ── */}
      <section className="bg-gray-50/50 py-8">
        <div className="page-container">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <h1 className="text-[16px] sm:text-[18px] font-bold text-gray-900 mb-3" style={{ fontFamily: "'Lora', serif" }}>
              Top Indian &amp; South Asian Cafes in Dallas–Fort Worth
            </h1>

            <p className="text-[12px] sm:text-[13px] text-gray-500 mb-6" style={{ fontFamily: "'Lora', serif" }}>
              Discover Indian, Pakistani, and South Asian cafes across the Dallas–Fort Worth area with DallasDesiScoop. Find chai cafes, coffee shops, filter kaapi spots, Irani cafes, fusion dessert cafes, and snack spots in Dallas, Frisco, Plano, Irving, McKinney, Allen, Carrollton, Richardson, Lewisville, and surrounding DFW communities.
            </p>

            {/* Cafe grid */}
            <AdminAddBar mc={mc} fields={CAFE_FIELDS} label="Add Cafe" />

            {filteredCafes.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {filteredCafes.map((c: any, i: number) => (
                  <AdminItem key={c._mcId} mc={mc} item={c} fields={CAFE_FIELDS} title="Cafe">
                    <CafeCard cafe={c} index={i} />
                  </AdminItem>
                ))}
              </div>
            )}

            {filteredCafes.length === 0 && (
              <div className="text-center py-16">
                <div className="text-4xl mb-3">☕</div>
                <p className="text-[15px] font-semibold text-gray-700 mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>Cafes coming soon.</p>
                <p className="text-[12px] text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>Know a great Desi cafe in DFW? <a href="/add-business" className="text-amber-600 font-semibold hover:underline">Submit a business</a> to get it listed.</p>
                {hasActiveFilters && (
                  <button
                    onClick={() => { setSelectedLocation(null); setSelectedHeroLocation('All Locations'); }}
                    className="mt-4 px-5 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-600 text-[12px] font-semibold hover:bg-amber-100 transition-colors"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}

            <p className="text-[12px] sm:text-[13px] text-gray-500 mt-8" style={{ fontFamily: "'Lora', serif" }}>
              Looking for chai cafes, South Indian coffee, filter kaapi, Irani cafes, Indian snack spots, or dessert cafes near you in DFW? DallasDesiScoop helps you discover local cafes serving masala chai, South Indian filter coffee, samosas, puffs, buns, and fusion drinks throughout the Dallas–Fort Worth area.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
